(ns ui.modal
  (:require ["@radix-ui/react-portal" :as RadixPortal]
            ["lucide-react" :refer [Fullscreen Loader2 Minimize X]]
            ["react" :as react]
            ["react-resize-detector" :refer [useResizeDetector]]
            ["react-rnd" :refer [Rnd]]
            [applied-science.js-interop :as js-interop]
            [react-hotkeys-hook :refer [useHotkeys]]
            [reagent.core :as reagent]
            [ui.button :as button]
            [ui.utils :refer [separate-props]]))

(def default-modal-params
  {:height 800 :width 800
   :x nil :y nil
   :max-size {:height 0 :width 0}
   :index 0})

(defonce modal-state (reagent/atom {}))

(def partial-merge (partial merge-with merge))

(defn get-viewport-size []
  {:height (or (js-interop/get-in js/window [:visualViewport :height])
               (js-interop/get js/window :innerHeight))
   :width  (or (js-interop/get-in js/window [:visualViewport :width])
               (js-interop/get js/window :innerWidth))})

(defn clamp-modal-to-viewport [modal-id]
  (let [{viewport-height :height viewport-width :width} (get-viewport-size)
        modal           (get @modal-state modal-id)
        width           (or (:width modal) (:width default-modal-params))
        height          (or (:height modal) (:height default-modal-params))
        width'          (min width viewport-width)
        height'         (min height viewport-height)

        base-update     {modal-id {:max-size {:height viewport-height
                                              :width viewport-width}
                                   :height height'
                                   :width width'}}

        positioned-update
        (cond-> base-update
          (some? (:x modal))
          (assoc-in [modal-id :x]
                    (max 0 (min (:x modal)
                                (- viewport-width width'))))

          (some? (:y modal))
          (assoc-in [modal-id :y]
                    (max 0 (min (:y modal)
                                (- viewport-height height')))))]
    (swap! modal-state partial-merge positioned-update)))

(defn update-max-size [modal-id]
  (clamp-modal-to-viewport modal-id))


(defn maximize [modal-id]
  (let [{:keys [height width]} (get-viewport-size)]
    (swap! modal-state partial-merge
           {modal-id {:width width
                      :height height
                      :x 0
                      :y 0}})))


(defn medium-size [modal-id]
  (let [{:keys [height width]} (get-viewport-size)]
    (swap! modal-state partial-merge
           {modal-id {:width (* width 0.8)
                      :height (* height 0.8)
                      :x (* width 0.1)
                      :y (* height 0.1)}})))


(defn toggle-size [modal-id]
  (let [{:keys [height width]} (get-viewport-size)]

    (if (or (= height (get-in @modal-state [modal-id :height]))
            (= width  (get-in @modal-state [modal-id :width])))
      (medium-size modal-id)
      (maximize modal-id))))


(def safety-overlay-id "sql-console-modal-safety-overlay")

(defn show-safety-overlay []
  (let [nodes (js-interop/call js/document :getElementsByClassName safety-overlay-id)]
    (doall
     (map #(js-interop/call-in % [:classList :remove] "hidden")
          nodes))))

(defn hide-safety-overlay []
  (let [nodes (js-interop/call js/document :getElementsByClassName safety-overlay-id)]
    (doall
     (map #(js-interop/call-in % [:classList :add] "hidden")
          nodes))))


(defn header [{:keys [loading? title modal-id on-close modal-props]
               :or {title "Modal"}}]
  [:header {:class ["bg-kleene-secondary-02"
                    "border-b border-kleene-border"
                    "rounded-t text-white px-2 py-2"
                    "flex items-center justify-between w-full"
                    "cursor-grab active:cursor-grabbing
                    relative"]}
   [:div {:class ["absolute modal-draggable-header"
                  "top-[12px] left-[22px] bottom-0 right-[50px]"]}]
   [:span {:class "text-lg mt-1 ml-1.5 font-medium"}
    title]

   (when loading?
     [:> Loader2 {:class "text-kleene-primary-v2 animate-spin"
                  :size  20}])

   [:div {:class "flex flex-row gap-2"}
    [button/button {:icon     Minimize
                    :data-cy  "modal-header-minimize-btn"
                    :on-click (fn []
                                (medium-size modal-id)
                                (when (:on-resize-stop modal-props)
                                  ((:on-resize-stop modal-props))))
                    :class    "bg-kleene-secondary-01 hover:bg-kleene-secondary-01/80 text-white"
                    :title    "Medium size"
                    :variant  :empty
                    :size     :icon}]

    [button/button {:icon     Fullscreen
                    :data-cy  "modal-header-fullscreen-btn"
                    :on-click (fn []
                                (maximize modal-id)
                                (when (:on-resize-stop modal-props)
                                  ((:on-resize-stop modal-props))))
                    :class    "bg-kleene-secondary-01 hover:bg-kleene-secondary-01/80 text-white"
                    :title    "Maximize"
                    :variant  :empty
                    :size     :icon}]

    [button/button {:icon     X
                    :data-cy  "modal-header-close-btn"
                    :on-click #(when on-close (on-close))
                    :class    "bg-kleene-secondary-01 hover:bg-kleene-secondary-01/80 text-white"
                    :title    "Close window"
                    :variant  :empty
                    :size     :icon

                    ;; Autofocus on the close button, so that the user can
                    ;; just press Enter to close the window
                    :tabIndex 0
                    :auto-focus true}]]])


(defn find-max-index []
  (or
   (->> @modal-state
        vals
        (map :index)
        (apply max))
   0))


(defn bring-to-front [modal-id]
  (let [index (inc (find-max-index))]
    (swap! modal-state assoc-in [modal-id :index] index)))


(defn modal
  "Draggable, resizable window. Requires `:id`, `:open?`, and `:on-close`.
   Header title and loading state are configured via `:header-props`."
  [& args]
  (let [[props children] (separate-props args)

        modal-id (:id props)

        _ (when-not (get @modal-state modal-id)
            (swap! modal-state assoc modal-id
                   (assoc (merge default-modal-params
                                 (get props :initial-props {}))
                          :index (inc (find-max-index)))))

        _ (react/useEffect
           (fn []
             (let [handler #(clamp-modal-to-viewport modal-id)
                   visual-viewport (js-interop/get js/window :visualViewport)]
               (.addEventListener js/window "resize" handler)
               (when visual-viewport
                 (.addEventListener visual-viewport "resize" handler))

               (handler)

               (fn []
                 (.removeEventListener js/window "resize" handler)
                 (when visual-viewport
                   (.removeEventListener visual-viewport "resize" handler)))))
           #js [modal-id])

        open? (:open? props)

        window-width (js-interop/get js/window :innerWidth)
        window-height (js-interop/get js/window :innerHeight)

        height (get-in @modal-state [modal-id :height])
        width (get-in @modal-state [modal-id :width])

        ;; Open new window centered by default
        default-center-offset-x (js/Math.floor (/ (- window-width width) 2))
        default-center-offset-y (js/Math.floor (/ (- window-height height) 2))

        x (or (get-in @modal-state [modal-id :x])
              default-center-offset-x)
        y (or (get-in @modal-state [modal-id :y])
              default-center-offset-y)

        max-height (get-in @modal-state [modal-id :max-size :height])
        max-width (get-in @modal-state [modal-id :max-size :width])

        z-index (+ 1000 (get-in @modal-state [modal-id :index] 0))

        {:keys [ref]} (js->clj (useResizeDetector #js {:handleWidth  true
                                                       :handleHeight true
                                                       :refreshMode  "throttle"
                                                       :refreshRate  100
                                                       :onResize     (partial update-max-size modal-id)})
                               :keywordize-keys true)

        hotkey-ref (useHotkeys "esc" #(when (:on-close props)
                                        ((:on-close props))))]


    [:> RadixPortal/Root {:as-child true}
     [:div {:class "fixed left-0 top-0 right-0 z-1111"}
      ;; iframes tend to capture drag events. To prevent this, we need to over
      ;; any potential iframes with an overlay.
      [:div {:class ["absolute z-999 inset-0 h-screen w-screen bg-transparent hidden" safety-overlay-id]
             :ref ref}]

      (when open?
        [:> Rnd {:style #js {:zIndex (str z-index)}

                 :enableResizing #js {:top         true
                                      :topLeft     true
                                      :topRight    true
                                      :bottom      true
                                      :left        true
                                      :bottomLeft  true
                                      :right       true
                                      :bottomRight true}

                 :bounds "#modal-container"
                 :minWidth (min 500 window-width)
                 :minHeight (min 500 window-height)

                 :className "transform-gpu z-1000"

                 :size {:height height
                        :width  width}
                 :position {:x x :y y}

                 :enableUserSelectHack true

                 :dragHandleClassName "modal-draggable-header"

                 :resizeHandleWrapperClass "select-none"

                 :resizeHandleStyles {:top {:top "-1px" :zIndex (inc z-index) :height "2px"}
                                      :topLeft {:top 0 :left 0 :zIndex (+ 2 z-index)}
                                      :topRight {:top 0 :right 0 :zIndex (+ 2 z-index)}
                                      :bottom {:bottom "-1px" :height "2px" :zIndex (inc z-index)}
                                      :bottomLeft {:bottom 0 :left 0 :zIndex (+ 2 z-index)}
                                      :bottomRight {:bottom 0 :right 0 :zIndex (+ 2 z-index)}
                                      :left {:left "-1px" :width "2px" :zIndex (inc z-index)}
                                      :right {:right "-1px" :width "2px" :zIndex (inc z-index)}}

                 :resizeHandleClasses #js {:top ""
                                           :topLeft ""
                                           :topRight ""
                                           :bottom ""
                                           :bottomLeft ""
                                           :bottomRight "react-resizable-handle handle-se"
                                           :left ""
                                           :right ""}


                 :onDragStart (fn []
                                (show-safety-overlay)
                                (js-interop/call-in js/window [:app :classList :add] "select-none")
                                (bring-to-front modal-id))

                 :onDragStop (fn [e d]
                               (hide-safety-overlay)
                               (js-interop/call-in js/window [:app :classList :remove] "select-none")
                               (let [{:keys [x y]} (get @modal-state modal-id)
                                     dx (js-interop/get d :x)
                                     dy (js-interop/get d :y)

                                     ;; Using whole pixel numbers for size and
                                     ;; position should fix the blurriness of the
                                     ;; fonts inside the modal in Chrome

                                     dx' (js/Math.ceil dx)
                                     dy' (js/Math.ceil dy)]
                                 (swap! modal-state partial-merge {modal-id {:x dx' :y dy'}})))

                 :onResizeStart (fn []
                                  (show-safety-overlay)
                                  (js-interop/call-in js/window [:app :classList :add] "select-none"))

                 :onResizeStop (fn [e direction ref delta position]
                                 (hide-safety-overlay)

                                 (js-interop/call-in js/window [:app :classList :remove] "select-none")

                                 (let [{:keys [x y height width]} (get @modal-state modal-id)
                                       width'  (js/parseInt (js-interop/get-in ref [:style :width])  10)
                                       height' (js/parseInt (js-interop/get-in ref [:style :height]) 10)
                                       dh (js-interop/get delta :height)
                                       dw (js-interop/get delta :width)
                                       dx (js-interop/get position :x)
                                       dy (js-interop/get position :y)

                                       ;; Using whole pixel numbers for size and
                                       ;; position should fix the blurriness of the
                                       ;; fonts inside the modal in Chrome

                                       width'' (js/Math.ceil width')
                                       height'' (js/Math.ceil height')

                                       dx' (js/Math.ceil dx)
                                       dy' (js/Math.ceil dy)]
                                   (swap! modal-state partial-merge {modal-id {:height height''
                                                                               :width width''
                                                                               :x dx'
                                                                               :y dy'}}))

                                 (when (:on-resize-stop props)
                                   ((:on-resize-stop props))))

                 :onResize (fn [e direction ref delta position]
                             (let [{:keys [x y height width]} (get @modal-state modal-id)
                                   width'  (js/parseInt (js-interop/get-in ref [:style :width])  10)
                                   height' (js/parseInt (js-interop/get-in ref [:style :height]) 10)
                                   dh (js-interop/get delta :height)
                                   dw (js-interop/get delta :width)
                                   dx (js-interop/get position :x)
                                   dy (js-interop/get position :y)

                                   ;; Using whole pixel numbers for size and
                                   ;; position should fix the blurriness of the
                                   ;; fonts inside the modal in Chrome

                                   width'' (js/Math.ceil width')
                                   height'' (js/Math.ceil height')

                                   dx' (js/Math.ceil dx)
                                   dy' (js/Math.ceil dy)]
                               (swap! modal-state partial-merge {modal-id {:height height''
                                                                           :width width''
                                                                           :x dx'
                                                                           :y dy'}})))}

         [:div {:data-cy (str "modal-" modal-id)
                :class   ["h-full w-full fixed top-0 left-0"
                          "z-21 flex items-stretch"
                          "transform-gpu"
                          (when open? "animate-fade-up animate-duration-300")
                          (when-not open? "hidden")]
                :style   {:height  (str height "px")
                          :width   (str width "px")
                          :z-index z-index}
                :tabIndex -1
                #_#_:ref hotkey-ref}
          [:div {:class ["dark:bg-black bg-white rounded-xs border border-kleene-border"
                         "grow shadow-lg shadow-black/50"]}
           [:div {:class "w-full h-full flex flex-col"}


            [(or (:header args)
                 header)
             (merge (:header-props props)
                    {:modal-id modal-id
                     :on-close (:on-close props)
                     :modal-props props})]

            (when open?
              (into [:<>] children))

            (when (:footer props)
              [(:footer props)
               (:footer-props props)])]]]])]]))
